import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { FC, useEffect, useState } from 'react'
import { FormProvider, useController, useForm } from 'react-hook-form'

import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import {
  RequestByIdApiType,
  RequestTypeAlias,
  RequestTypeType
} from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import {
  confirmSubmitHeader,
  confirmSubmitMessageAuto,
  confirmSubmitMessagePerson,
  personsKey
} from '@/app/constants/constants'
import { useFilteredRequestTypes } from '@/components/entities/passes/hooks/useFilteredRequestTypes'
import { PERMANENT_PASS_TYPES } from '@/components/entities/passes/model/constants'
import { PassAutosFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassAutosFormBlock'
import { PassFormHeader } from '@/components/entities/passes/ui/PassForms/FormBlock/PassFormHeader'
import { PassPersonsFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassPersonsFormBlock'
import { PassTypesSelectorFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassTypesSelectorFormBlock'
import { PassParkingPlug } from '@/components/entities/passes/ui/PassParkingPlug'
import { ConfirmSubmitModalName } from '@/components/entities/requests/model/form.types'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import { useFormDirty } from '@/components/entities/requests/ui/hooks/useFormDirty'
import {
  formatAutosData,
  formatPersonsData,
  getDefaultAutos,
  getDefaultPersons,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'
import { ConfirmSubmitModal } from '@/components/shared/modal'
import { AppTextareaAutoSize } from '@/components/shared/textarea'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH_MESSAGE_FROM
} from '@/utils/constants'

import { useParkingPlaces } from '../../../requests/hooks/useParkingPlaces'
import {
  AutosApiData,
  AutosFormData,
  AutosFormErrors,
  CommonFormData,
  generateAutoPassFormSchema,
  generateCommonSchema,
  generatePersonsSchema,
  PersonsApiData,
  PersonsFormData,
  PersonsFormErrors
} from '../../../requests/ui/model/schemas'

const hasAutosFormData = ({ autos }: AutosFormData) =>
  autos.some(
    auto => Boolean(auto.number?.trim()) || Boolean(auto.model?.trim())
  )

const hasPersonsFormData = ({ persons }: PersonsFormData) =>
  persons.some(person => Boolean(person.value?.trim()))

type Props = {
  typeAliasFromUrl: RequestTypeAlias
  onFormDirty: (_: boolean) => void
  pass?: RequestByIdApiType
  onSubmit: (data: AutosApiData | PersonsApiData) => Promise<void>
  passError?: AxiosError<ServerValidationError> | null
  isPending?: boolean
}

const PermanentPassForm: FC<Props> = ({
  typeAliasFromUrl,
  onFormDirty,
  pass,
  onSubmit,
  passError,
  isPending
}) => {
  const { navigateToCreateFormPass } = useRequestNavigation()
  const { parkingPlaces } = useParkingPlaces()
  const [confirmSubmitModal, setConfirmSubmitModal] =
    useState<ConfirmSubmitModalName | null>(null)

  const permanentRequestTypes = useFilteredRequestTypes(PERMANENT_PASS_TYPES)

  const { rooms } = useSelectedProjectPremises()

  const isSelectedPassAuto =
    typeAliasFromUrl === RequestTypeAlias.PermanentPassAuto

  const commonForm = useForm<CommonFormData>({
    defaultValues: {
      description: undefined
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateCommonSchema())
  })

  const { field: descriptionField } = useController({
    control: commonForm.control,
    name: 'description'
  })

  const autosForm = useForm<AutosFormData, AutosFormErrors>({
    defaultValues: {
      room: undefined,
      autos: [
        {
          number: '',
          model: '',
          parkingPlace: parkingPlaces[0],
          isStandard: true
        }
      ]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateAutoPassFormSchema())
  })

  const personsForm = useForm<PersonsFormData, PersonsFormErrors>({
    defaultValues: {
      room: undefined,
      persons: [{ value: '' }]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generatePersonsSchema())
  })

  useFormDirty(onFormDirty, [
    commonForm.formState,
    autosForm.formState,
    personsForm.formState
  ])

  useEffect(() => {
    autosForm.reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      autos: getDefaultAutos(pass, parkingPlaces)
    })

    personsForm.reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      persons: getDefaultPersons(pass)
    })

    commonForm.reset({
      description: pass?.description ?? ''
    })
  }, [autosForm, commonForm, parkingPlaces, pass, personsForm, rooms])

  const handleSelectedPassTypeAliasChange = (option: RequestTypeType) => {
    navigateToCreateFormPass(option.alias)
  }

  const handleFormSubmitAutos = async (data: AutosFormData) => {
    if (
      hasPersonsFormData(personsForm.getValues()) &&
      confirmSubmitModal === null
    ) {
      setConfirmSubmitModal(ConfirmSubmitModalName.Person)
      return
    }
    await onSubmit(formatAutosData(data, commonForm.getValues().description))
    setConfirmSubmitModal(null)
  }

  const handleFormSubmitPersons = async (data: PersonsFormData) => {
    if (
      hasAutosFormData(autosForm.getValues()) &&
      confirmSubmitModal === null
    ) {
      setConfirmSubmitModal(ConfirmSubmitModalName.Auto)
      return
    }

    await onSubmit(formatPersonsData(data, commonForm.getValues().description))
    setConfirmSubmitModal(null)
  }

  if (isSelectedPassAuto && parkingPlaces.length === 0) {
    return <PassParkingPlug isPermanent />
  }

  const handleFormSubmit = isSelectedPassAuto
    ? autosForm.handleSubmit(handleFormSubmitAutos)
    : personsForm.handleSubmit(handleFormSubmitPersons)

  return (
    <>
      <DrawerFormLayout
        onSubmit={handleFormSubmit}
        isPending={isPending}
        header={
          <PassFormHeader text="Постоянный пропуск на территорию для вас, членов вашей семьи, доверенных лиц, персонала и вашего транспорта" />
        }
      >
        <div className="form-block-cover mb-16 p-32 lg:p-16">
          {isSelectedPassAuto ? (
            <FormProvider {...autosForm}>
              <RequestRoomPickerFormBlock
                key={RequestTypeAlias.PermanentPassAuto}
              />
            </FormProvider>
          ) : (
            <FormProvider {...personsForm}>
              <RequestRoomPickerFormBlock key={personsKey} />
            </FormProvider>
          )}
        </div>
        <div className="form-block-cover mb-16 px-32 py-24 lg:p-16">
          <p className="form-block-title mb-16">Выберите тип пропуска</p>
          <PassTypesSelectorFormBlock
            name="pass-types"
            options={permanentRequestTypes}
            alias={typeAliasFromUrl}
            onChange={handleSelectedPassTypeAliasChange}
          />
        </div>

        <div className="form-block-cover mb-16 p-32 lg:p-16">
          <AppTextareaAutoSize
            {...descriptionField}
            maxLength={MAX_DESCRIPTION_LENGTH}
            maxLengthMessageFrom={MAX_DESCRIPTION_LENGTH_MESSAGE_FROM}
            placeholder="Описание"
            className="!min-h-[56px] w-full overflow-auto border border-grey-20"
            inputClassName="!min-h-[56px]"
          />
        </div>
        {isSelectedPassAuto ? (
          <FormProvider {...autosForm}>
            <PassAutosFormBlock
              typeAlias={RequestTypeAlias.PermanentPassAuto}
              passError={passError}
            />
          </FormProvider>
        ) : (
          <FormProvider {...personsForm}>
            <PassPersonsFormBlock
              passError={passError}
              modalHeader="Удалить пропуск?"
            />
          </FormProvider>
        )}
      </DrawerFormLayout>
      {confirmSubmitModal && (
        <ConfirmSubmitModal
          opened={Boolean(confirmSubmitModal)}
          header={confirmSubmitHeader}
          message={
            confirmSubmitModal === ConfirmSubmitModalName.Person
              ? confirmSubmitMessagePerson
              : confirmSubmitMessageAuto
          }
          closeButtonText="Вернуться к заявке"
          submitButtonText="Отправить"
          onClose={() => setConfirmSubmitModal(null)}
          onSubmit={handleFormSubmit}
        />
      )}
    </>
  )
}

export { PermanentPassForm }
